/**
 * Display formatting and input parsing — **presentation only, no money maths**.
 *
 * Every value the terminal shows arrives from the server as a canonical decimal
 * string (docs/10 §2.6): prices ×1e8, volumes ×1e4, money in account-currency
 * minor units, margin level in basis points of percent. Nothing here multiplies
 * a price by a volume, converts a currency, or computes P&L: those numbers come
 * from the server (`/preview`, `tryAssessAccount`) precisely so there is one
 * implementation of each.
 *
 * The helpers fall into three groups:
 *
 * 1. **Decimal rendering** (`formatScaled`, `formatMoney`, `formatVolume`, …):
 *    string surgery — insert a decimal point, pad, group thousands. Exact, and
 *    it never round-trips through a JS `number`, so `0.1 + 0.2` cannot happen.
 * 2. **Signed deltas** (`formatSignedScaled`, `formatSignedMoney`): the label
 *    carries `+`/`−` and an arrow as well as a colour, because colour alone is
 *    not a signal (WCAG 1.4.1).
 * 3. **Input parsing** (`parseScaledInput`, `parseVolumeInput`, `parsePriceInput`):
 *    a trader types `1.08425` or `0.10`; these turn that into the scaled integer
 *    the wire wants. They validate against the symbol's own step/tick/min/max,
 *    which the server sent — they never compute a price or a volume *for* the
 *    user.
 *
 * `BigInt` appears only where an exact comparison or addition is unavoidable
 * (a delta, a step multiple, a running balance). A JS `number` appears nowhere:
 * not even for the grid, which is a string.
 */

/** Split a scaled integer string into sign, integer digits and a padded fraction. */
function splitScaled(value: string, digits: number): {
  negative: boolean;
  whole: string;
  fraction: string;
} {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const magnitude = negative ? trimmed.slice(1) : trimmed;
  const padded = magnitude.padStart(digits + 1, '0');
  const whole = padded.slice(0, padded.length - digits);
  const fraction = digits === 0 ? '' : padded.slice(padded.length - digits);
  return { negative, whole, fraction };
}

/** Group an integer digit string in threes: `1234567` → `1,234,567`. */
export function groupThousands(whole: string): string {
  let out = '';
  let count = 0;
  for (let index = whole.length - 1; index >= 0; index -= 1) {
    out = whole[index] + out;
    count += 1;
    if (count % 3 === 0 && index > 0) out = `,${out}`;
  }
  return out;
}

/** `"-12345"`, 5 → `"-0.12345"`; `formatScaled("100000", 2)` → `"1000.00"`. */
export function formatScaled(value: string, digits: number): string {
  const { negative, whole, fraction } = splitScaled(value, digits);
  const grouped = groupThousands(whole);
  return digits === 0 ? `${negative ? '-' : ''}${grouped}` : `${negative ? '-' : ''}${grouped}.${fraction}`;
}

/**
 * Fixed-point rendering for a value scaled by `10^scale`, shown with `display`
 * decimals.
 *
 * Prices and volumes are **not** scaled by their display digits: a price is
 * always ×1e8 and a volume ×1e4, while the decimals a trader sees come from the
 * instrument (5 for EURUSD, 2 for XAUUSD). If the price is off the instrument's
 * tick grid, the extra digits are kept rather than truncated — showing `1.08425`
 * for a stored `1.084252` would be a quiet lie about the price.
 */
export function formatFixed(value: string, scale: number, display: number): string { // no-float-ok: decimal-place counts, not amounts
  const { negative, whole, fraction } = splitScaled(value, scale);
  const shown = fraction.slice(0, display);
  const rest = fraction.slice(display);
  const decimals = /^0*$/.test(rest) ? shown : fraction;
  const sign = negative ? '-' : '';
  return decimals.length === 0 ? `${sign}${whole}` : `${sign}${whole}.${decimals}`;
}

/** A price: ×1e8 on the wire, `digits` decimals on screen, no grouping. */
export function formatPrice(value: string | null | undefined, digits: number): string {
  if (value === null || value === undefined) return '—';
  return formatFixed(value, 8, digits);
}

/**
 * A price truncated to the instrument's display precision — **watchlist only**.
 *
 * The simulated feed quotes a mid that moves in finer steps than the declared
 * tick (a spread of 1.2 pips around a mid offset by half a tick), so an exact
 * rendering can need 8 decimals and no column layout survives that. The
 * watchlist is a scanning surface and prints the instrument's own precision.
 *
 * Every value a trader can *act* on — the ticket's prices, a position's open or
 * SL/TP, a deal's price — uses `formatPrice`, which never truncates: an SL shown
 * as `1919.99` and sent back as `1919.99` would silently move the stop.
 */
export function formatPriceCompact(value: string | null | undefined, digits: number): string {
  if (value === null || value === undefined) return '—';
  const { negative, whole, fraction } = splitScaled(value, 8);
  const shown = fraction.slice(0, digits);
  return digits === 0 ? `${negative ? '-' : ''}${whole}` : `${negative ? '-' : ''}${whole}.${shown}`;
}

/**
 * Display decimals for one instrument: its own `digits`, but never fewer than
 * the tick grid needs.
 *
 * A row's declared precision can be coarser than its tick — the admin console
 * refuses a tick the declared precision cannot represent (`E_TICK_PRECISION`),
 * but an older row or an import can still arrive off that grid — so showing
 * exactly `digits` would print `1919.99` for a price of `1919.993`: a column
 * that looks precise and is wrong. The grid wins; a price on the grid always
 * prints exactly.
 */
export function priceDigits(digits: number, tickSizeE8: string): number { // no-float-ok: digit counts, not amounts
  let tick = BigInt(tickSizeE8);
  if (tick <= 0n) return digits;
  let trailingZeros = 0;
  while (trailingZeros < 8 && tick % 10n === 0n) {
    tick /= 10n;
    trailingZeros += 1;
  }
  const gridDecimals = 8 - trailingZeros;
  return digits >= gridDecimals ? digits : gridDecimals;
}

/**
 * Decimal places a volume step needs: step `100` (0.01 lots) → 2, `10` → 3,
 * `1000` → 1, `10000` → 0. A digit count, derived from the step string.
 */
export function volumeDecimals(stepE4: string): number { // no-float-ok: a decimal-place count, not an amount
  const step = stepE4.padStart(5, '0');
  for (let decimals = 0; decimals < 4; decimals += 1) {
    const divisor = 10n ** BigInt(4 - decimals);
    if (BigInt(step) % divisor === 0n) return decimals;
  }
  return 4;
}

/** `"1000"` (0.10 lots) → `"0.10"`; the step decides the decimal places. */
export function formatVolume(value: string, stepE4 = '100'): string {
  return formatFixed(value, 4, volumeDecimals(stepE4));
}

/** Account money, grouped: `"1234567"` minor, 2 digits → `"12,345.67"`. */
export function formatMoney(value: string | null | undefined, digits: number): string {
  if (value === null || value === undefined) return '—';
  return formatScaled(value, digits);
}

/** A signed money delta with an explicit sign and arrow: `+1,234.56 ↑`. */
export function formatSignedMoney(value: string | null | undefined, digits: number): string {
  if (value === null || value === undefined) return '—';
  const isNegative = value.startsWith('-');
  const magnitude = isNegative ? value.slice(1) : value;
  const body = formatScaled(magnitude, digits);
  if (value === '0' || /^0*$/.test(magnitude)) return `${body}`;
  return isNegative ? `−${body} ↓` : `+${body} ↑`;
}

/** A signed price delta (market-watch change), in price digits. */
export function formatSignedPrice(
  value: string | null | undefined,
  digits: number,
  compact = false,
): string {
  if (value === null || value === undefined) return '—';
  const isNegative = value.startsWith('-');
  const magnitude = isNegative ? value.slice(1) : value;
  const body = compact ? formatPriceCompact(magnitude, digits) : formatPrice(magnitude, digits);
  if (/^0+$/.test(magnitude)) return body;
  return isNegative ? `−${body} ↓` : `+${body} ↑`;
}

/** A price difference in **points** (ticks): integer division, display only. */
export function pointsBetween(left: string, right: string, tickSizeE8: string): string {
  const tick = BigInt(tickSizeE8);
  if (tick === 0n) return '0';
  const difference = BigInt(left) - BigInt(right);
  return (difference / tick).toString();
}

/** Percent × 100 → `"123.45%"`; `null` is the infinite level, never a number. */
export function formatPercentBp(bp: string | null | undefined): string {
  if (bp === null || bp === undefined) return '∞';
  const { negative, whole, fraction } = splitScaled(bp, 2);
  const grouped = groupThousands(whole);
  return `${negative ? '-' : ''}${grouped}.${fraction}%`;
}

/** A spread in points from two e8 prices: display-only integer subtraction. */
export function spreadPoints(bid: string, ask: string, tickSizeE8: string): string {
  const tick = BigInt(tickSizeE8);
  if (tick === 0n) return '0';
  return ((BigInt(ask) - BigInt(bid)) / tick).toString();
}

/** Change from the session reference, as an e8 difference (may be negative). */
export function priceDelta(value: string, reference: string): string {
  return (BigInt(value) - BigInt(reference)).toString();
}

/**
 * Snap a price down or up to the instrument's tick grid.
 *
 * An **input helper**, not a trading decision: the feed's quotes sit on a
 * half-spread offset, so the live price can be off the 0.0001 grid the engine
 * requires, and prefilling a price field with it would make the default order
 * invalid. The ticket prefills a grid-aligned touch price (a limit away from the
 * market, a stop beyond it) and the server validates the value again. Nothing
 * downstream treats the snapped price as a market price.
 */
export function snapToTick(valueE8: string, tickE8: string, direction: 'down' | 'up'): string {
  const tick = BigInt(tickE8);
  if (tick <= 0n) return valueE8;
  const value = BigInt(valueE8);
  const remainder = value % tick;
  if (remainder === 0n) return valueE8;
  const base = value - remainder;
  return (direction === 'up' ? base + tick : base).toString();
}

/** `"1.08425"` → `"108425000"` (8 decimals), or null when it is not a number. */
export function parseScaledInput(text: string, scale: number): string | null { // no-float-ok: scale is a decimal-place count
  const trimmed = text.trim().replace(',', '.');
  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(trimmed);
  if (match === null) return null;
  const sign = match[1] === '-' ? '-' : '';
  const whole = match[2] ?? '';
  const fraction = match[3] ?? '';
  if (whole.length === 0 && fraction.length === 0) return null;
  if (fraction.length > scale) {
    if (!/^0*$/.test(fraction.slice(scale))) return null;
  }
  const paddedFraction = `${fraction.slice(0, scale)}${'0'.repeat(scale)}`.slice(0, scale);
  const combined = `${whole.length === 0 ? '0' : whole}${paddedFraction}`;
  const magnitude = BigInt(combined);
  return magnitude === 0n ? '0' : `${sign}${magnitude.toString()}`;
}

export type PriceCheck =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly message: string };

/** Parse a price (wire scale 1e8) and check the symbol's own tick grid. */
export function parsePriceInput(
  text: string,
  digits: number,
  tickSizeE8: string,
  label = 'Price',
): PriceCheck {
  const trimmed = text.trim().replace(',', '.');
  const decimals = /^\d*\.(\d*)$/.exec(trimmed)?.[1] ?? '';
  const scaled = parseScaledInput(trimmed, 8);
  if (scaled === null) {
    return { ok: false, message: `${label}: enter a number` };
  }
  if (decimals.length > digits && !/^0*$/.test(decimals.slice(digits))) {
    return { ok: false, message: `${label}: use at most ${digits} decimals` };
  }
  if (BigInt(scaled) <= 0n) return { ok: false, message: `${label}: must be greater than zero` };
  const tick = BigInt(tickSizeE8);
  if (tick > 0n && BigInt(scaled) % tick !== 0n) {
    return {
      ok: false,
      message: `${label}: must be a multiple of ${formatPrice(tickSizeE8, digits)}`,
    };
  }
  return { ok: true, value: scaled };
}

export type VolumeCheck =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly message: string };

/**
 * Parse a lot size against the symbol's min/max/step (all server-supplied).
 *
 * This is form validation, not volume maths: the engine re-checks the same
 * bounds and answers `E_INVALID_VOLUME` if they disagree.
 */
export function parseVolumeInput(
  text: string,
  stepE4: string,
  minE4: string,
  maxE4: string,
): VolumeCheck {
  const scaled = parseScaledInput(text, 4);
  if (scaled === null) return { ok: false, message: `Volume: enter lots, e.g. 0.10` };
  const value = BigInt(scaled);
  if (value <= 0n) return { ok: false, message: 'Volume: must be greater than zero' };
  if (value < BigInt(minE4)) {
    return { ok: false, message: `Volume: minimum is ${formatVolume(minE4, stepE4)} lots` };
  }
  if (value > BigInt(maxE4)) {
    return { ok: false, message: `Volume: maximum is ${formatVolume(maxE4, stepE4)} lots` };
  }
  if (value % BigInt(stepE4) !== 0n) {
    return { ok: false, message: `Volume: must be a multiple of ${formatVolume(stepE4, stepE4)} lots` };
  }
  return { ok: true, value: scaled };
}

/** The next valid volume in `direction` (+1/−1), clamped to min/max. */
export function stepVolume(
  currentE4: string,
  direction: 1 | -1,
  stepE4: string,
  minE4: string,
  maxE4: string,
): string {
  const step = BigInt(stepE4);
  const current = BigInt(currentE4);
  const next = current + BigInt(direction) * step;
  if (next < BigInt(minE4)) return minE4;
  if (next > BigInt(maxE4)) {
    // Snap to the largest valid multiple at or below max.
    const max = BigInt(maxE4);
    return (max - ((max - BigInt(minE4)) % step)).toString();
  }
  return next.toString();
}

/** A server timestamp in the server's clock, to the millisecond. */
export function formatServerTime(iso: string): string {
  // `2026-10-23T13:44:21.697Z` → `13:44:21`
  const time = iso.slice(11, 19);
  return time.length === 8 ? time : iso;
}

/** A timestamp as a desk reads it: `2026-10-23 13:44:21`. */
export function formatTimestamp(iso: string): string {
  if (iso.length < 19) return iso;
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
}
